import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useStore } from "@/lib/store";

export default function Auth() {
  const [tab, setTab] = useState("login");
  const [meta, setMeta] = useState({ zones: [] });
  const [form, setForm] = useState({
    name: "", email: "", password: "", role: "buyer",
    company: "", gstin: "", zone_id: "", address: "", shop_name: "",
  });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const { login, register } = useStore();
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/meta").then((r) => {
      setMeta(r.data);
      if (r.data.zones.length) setForm((f) => ({ ...f, zone_id: f.zone_id || r.data.zones[0].id }));
    }).catch(() => {});
  }, []);

  const go = (u) => navigate(u.role === "vendor" ? "/vendor" : u.role === "admin" ? "/admin" : "/buyer");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const u = tab === "login"
        ? await login(form.email, form.password)
        : await register(form);
      toast.success(tab === "login" ? "Welcome back" : "Account created");
      go(u);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const googleSignIn = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/buyer";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] bg-slate-50 py-12" data-testid="auth-page">
      <div className="max-w-md mx-auto card-surface p-8">
        <h1 className="font-display text-2xl font-bold text-slate-900">
          {tab === "login" ? "Sign in" : "Create account"}
        </h1>
        <p className="text-sm text-slate-500 mt-1">B2B procurement for interior materials — Bengaluru</p>
        <div className="flex gap-2 mt-6 mb-6">
          {["login", "register"].map((t) => (
            <button key={t} data-testid={`auth-tab-${t}`} onClick={() => { setTab(t); setError(""); }}
              className={`flex-1 rounded-lg border px-4 py-2 text-sm font-semibold capitalize ${tab === t ? "border-slate-900 bg-slate-900 text-white" : "border-slate-300 text-slate-600"}`}>
              {t}
            </button>
          ))}
        </div>
        {error && <div data-testid="auth-error" className="mb-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>}
        <button type="button" onClick={googleSignIn} data-testid="google-signin-btn"
          className="w-full flex items-center justify-center gap-3 rounded-lg border border-slate-300 bg-white px-4 py-3 text-sm font-semibold text-slate-700 hover:bg-slate-50 transition-colors">
          <svg className="h-5 w-5" viewBox="0 0 24 24" aria-hidden="true">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/>
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z"/>
            <path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84z"/>
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z"/>
          </svg>
          Continue with Google
        </button>
        <div className="flex items-center gap-3 my-5">
          <span className="h-px flex-1 bg-slate-200" />
          <span className="text-xs text-slate-400">or use email</span>
          <span className="h-px flex-1 bg-slate-200" />
        </div>
        <form onSubmit={submit} className="space-y-4">
          {tab === "register" && (
            <>
              <div className="grid grid-cols-2 gap-2">
                {["buyer", "vendor"].map((r) => (
                  <button type="button" key={r} data-testid={`register-role-${r}`} onClick={() => setForm({ ...form, role: r })}
                    className={`rounded-lg border px-3 py-2.5 text-sm font-semibold capitalize ${form.role === r ? "border-orange-700 bg-orange-50 text-orange-800" : "border-slate-300 text-slate-600"}`}>
                    {r === "buyer" ? "Buyer (interior firm)" : "Vendor / distributor"}
                  </button>
                ))}
              </div>
              <input data-testid="register-name-input" required placeholder="Full name" value={form.name} onChange={set("name")} className="input-b2b" />
              {form.role === "vendor" && (
                <input data-testid="register-shop-input" required placeholder="Shop / firm name" value={form.shop_name} onChange={set("shop_name")} className="input-b2b" />
              )}
              <input data-testid="register-company-input" placeholder="Company" value={form.company} onChange={set("company")} className="input-b2b" />
              <input data-testid="register-gstin-input" placeholder="GSTIN (for invoices)" value={form.gstin} onChange={set("gstin")} className="input-b2b" />
              <select data-testid="register-zone-select" value={form.zone_id} onChange={set("zone_id")} className="input-b2b">
                {meta.zones.map((z) => <option key={z.id} value={z.id}>{z.name}, Bengaluru</option>)}
              </select>
              <input data-testid="register-address-input" placeholder="Address" value={form.address} onChange={set("address")} className="input-b2b" />
              <input data-testid="register-phone-input" placeholder="WhatsApp number (+91…) for order alerts" value={form.phone || ""} onChange={set("phone")} className="input-b2b" />
            </>
          )}
          <input data-testid="auth-email-input" required type="email" placeholder="Email" value={form.email} onChange={set("email")} className="input-b2b" />
          <input data-testid="auth-password-input" required type="password" minLength={6} placeholder="Password" value={form.password} onChange={set("password")} className="input-b2b" />
          <button type="submit" disabled={busy} data-testid="auth-submit-btn" className="btn-brand w-full !py-3">
            {busy ? "Please wait…" : tab === "login" ? "Sign in" : "Create account"}
          </button>
        </form>
        <p className="mt-6 text-xs text-slate-400 leading-relaxed">
          Demo accounts — Buyer: buyer@demo.com / Buyer@123 · Vendor: vendor@demo.com / Vendor@123
        </p>
      </div>
    </div>
  );
}
